import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { FOOTER_LINK_COLUMNS, linksForFooterColumn, withFooterReferralParams } from '../lib/dankoreNetworkLinks';
import './site-brand-lockup.js';
import { contactHref, resolveRouterPath } from '../lib/router-path';

/** Full multi-column site footer (home, blog, about, etc.). */
@customElement('site-footer')
export class SiteFooter extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .footer-wrap {
      box-sizing: border-box;
      width: 100%;
      max-width: var(--layout-max-width, 1120px);
      margin: 0 auto;
      padding-left: max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-left, 0px));
      padding-right: max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-right, 0px));
    }

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .site-footer {
      margin-top: 3.5rem;
      padding-top: 2.75rem;
      padding-bottom: 2rem;
      border-top: 1px solid var(--border-subtle, #343434);
    }

    .footer-main {
      display: grid;
      grid-template-columns: minmax(0, 200px) minmax(0, 1fr);
      gap: 2rem 3.5rem;
      align-items: start;
      margin-bottom: 2.25rem;
    }

    .footer-brand {
      align-self: start;
      min-width: 0;
    }

    .footer-columns {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 1.5rem 2.25rem;
    }

    .footer-col-link--site {
      color: var(--brand-accent-soft, #fdc06a);
      font-weight: 600;
    }

    .footer-col-link--site:hover {
      color: var(--brand-accent, #fb9716);
    }

    .footer-col {
      min-width: 0;
    }

    .footer-col-title {
      font-size: 0.8125rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--text);
      margin: 0 0 1rem;
      line-height: 1.25;
    }

    .footer-col-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .footer-col-list li {
      margin: 0 0 0.7rem;
    }

    .footer-col-list li:last-child {
      margin-bottom: 0;
    }

    .footer-col-link {
      font-size: 0.875rem;
      line-height: 1.4;
      color: var(--text-muted);
      text-decoration: none;
      transition: color 0.15s ease;
    }

    .footer-col-link:hover {
      color: var(--accent);
    }

    .footer-rule {
      height: 1px;
      margin: 0 0 1.25rem;
      background: var(--border-card, rgba(65, 65, 65, 0.35));
      border: none;
    }

    .footer-bar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 1rem 1.5rem;
      font-size: 0.8125rem;
      color: var(--text-muted);
      line-height: 1.5;
    }

    .footer-bar-left {
      margin: 0;
    }

    .footer-bar-right {
      margin: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-end;
      gap: 0.75rem 1.25rem;
    }

    .footer-social-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--text-muted);
      text-decoration: none;
      transition: color 0.15s ease;
    }

    .footer-social-icon:hover {
      color: var(--accent);
    }

    .footer-social-icon svg {
      width: 1.35rem;
      height: 1.35rem;
      display: block;
    }

    .footer-bar-right .footer-license {
      color: var(--text-muted);
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .footer-bar-right .footer-license:hover {
      color: var(--accent);
    }

    .footer-bar-right .footer-internal-link {
      color: var(--brand-accent-soft, #fdc06a);
      text-decoration: none;
      font-weight: 700;
      font-size: 0.9375rem;
    }

    .footer-bar-right .footer-internal-link:hover {
      color: var(--brand-accent, #fb9716);
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .footer-bar-sep {
      color: var(--border-card, rgba(65, 65, 65, 0.5));
      user-select: none;
    }

    @media (max-width: 1020px) {
      .footer-columns {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }

    @media (max-width: 720px) {
      .footer-columns {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 840px) {
      .footer-main {
        grid-template-columns: 1fr;
        justify-items: center;
        text-align: center;
        gap: 1.75rem;
      }

      .footer-brand {
        justify-self: center;
      }

      .footer-columns {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        width: 100%;
        max-width: 520px;
        text-align: left;
      }

      .footer-bar {
        flex-direction: column;
        text-align: center;
      }

      .footer-bar-right {
        justify-content: center;
      }
    }

    @media (max-width: 480px) {
      .footer-columns {
        grid-template-columns: 1fr;
        max-width: none;
      }
    }
  `;

  render() {
    return html`
      <div class="footer-wrap">
        <footer class="site-footer">
          <h2 class="visually-hidden">Site footer</h2>
          <div class="footer-main">
            <site-brand-lockup
              class="footer-brand"
              layout="stacked"
              size="sm"
            ></site-brand-lockup>
            <nav class="footer-columns" aria-label="Related sites and tools">
              <section class="footer-col" aria-labelledby="footer-site-h">
                <h3 class="footer-col-title" id="footer-site-h">This site</h3>
                <ul class="footer-col-list">
                  <li>
                    <a class="footer-col-link footer-col-link--site" href="${resolveRouterPath('services')}">Services</a>
                  </li>
                  <li>
                    <a class="footer-col-link footer-col-link--site" href="${resolveRouterPath('blog')}">Blog</a>
                  </li>
                  <li>
                    <a class="footer-col-link" href="${resolveRouterPath('about')}">About</a>
                  </li>
                  <li>
                    <a class="footer-col-link footer-col-link--site" href="${contactHref()}">Contact</a>
                  </li>
                  <li>
                    <a class="footer-col-link" href="${resolveRouterPath('terms')}">Terms of Use</a>
                  </li>
                  <li>
                    <a class="footer-col-link" href="${resolveRouterPath('privacy')}">Privacy Policy</a>
                  </li>
                </ul>
              </section>
              ${FOOTER_LINK_COLUMNS.map(
                (col, i) => html`
                  <section class="footer-col" aria-labelledby=${`footer-col-h-${i}`}>
                    <h3 class="footer-col-title" id=${`footer-col-h-${i}`}>${col.title}</h3>
                    <ul class="footer-col-list">
                      ${linksForFooterColumn(col.ids).map(
                        (link) => html`
                          <li>
                            <a
                              class="footer-col-link"
                              href=${withFooterReferralParams(link.url, link.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              >${link.label}</a
                            >
                          </li>
                        `
                      )}
                    </ul>
                  </section>
                `
              )}
            </nav>
          </div>
          <hr class="footer-rule" />
          <div class="footer-bar">
            <p class="footer-bar-left">© 2026 Yes We Can LLC. All rights reserved.</p>
            <div class="footer-bar-right">
              <a class="footer-internal-link" href="${resolveRouterPath('blog')}">Blog</a>
              <span class="footer-bar-sep" aria-hidden="true">|</span>
              <a
                class="footer-social-icon"
                href="https://www.facebook.com/yeswecanllc/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Yes We Can LLC on Facebook"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path
                    d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 5.996 4.388 10.963 10.125 11.858v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.036 24 18.062 24 12.073z"
                  />
                </svg>
              </a>
              <span class="footer-bar-sep" aria-hidden="true">|</span>
              <a
                class="footer-license"
                href="https://www.pexels.com/license/"
                target="_blank"
                rel="noopener noreferrer"
                >Stock photos: Pexels license</a
              >
            </div>
          </div>
        </footer>
      </div>
    `;
  }
}
